import React, {
  useState,
  useRef,
  useEffect,
  useCallback,
  useMemo,
} from "react";
import {
  Box,
  Paper,
  TextField,
  IconButton,
  Typography,
  Avatar,
  Divider,
  CircularProgress,
  Alert,
  Chip,
} from "@mui/material";
import {
  Send as SendIcon,
  SmartToy as BotIcon,
  Person as PersonIcon,
  Stop as StopIcon,
  Clear,
} from "@mui/icons-material";
import { commonTextFieldProps } from "../../utils/styles";
import { getValidToken } from "../../utils/authUtils";
import axios from "axios";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeRaw from "rehype-raw";
import NavigationHeader from "../../components/NavigationHeader";
import { useNavigate } from "react-router-dom";

interface Message {
  id: string;
  text: string;
  isUser: boolean;
  timestamp: Date;
  isLoading?: boolean;
}

// Custom hook for auto-scrolling to bottom
const useChatScroll = (dep: Message[]) => {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (ref.current) {
      ref.current.scrollTop = ref.current.scrollHeight;
    }
  }, [dep]);

  return ref;
};

// Memoized Message Component for performance optimization
const MemoizedMessage = React.memo(({ message }: { message: Message }) => {
  const formatTime = useCallback((date: Date) => {
    return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  }, []);

  // Memoized markdown components for better performance
  const markdownComponents = useMemo(
    () => ({
      // Headings
      h1: ({ children }: any) => (
        <Typography
          variant="h6"
          component="h1"
          sx={{
            fontWeight: 600,
            color: "text.primary",
            mb: 1,
            mt: 1.5,
            fontSize: "1rem",
          }}
        >
          {children}
        </Typography>
      ),
      h2: ({ children }: any) => (
        <Typography
          variant="h6"
          component="h2"
          sx={{
            fontWeight: 600,
            color: "text.primary",
            mb: 0.8,
            mt: 1.2,
            fontSize: "0.9rem",
          }}
        >
          {children}
        </Typography>
      ),
      h3: ({ children }: any) => (
        <Typography
          variant="subtitle1"
          component="h3"
          sx={{
            fontWeight: 600,
            color: "text.primary",
            mb: 0.6,
            mt: 1,
            fontSize: "0.8rem",
          }}
        >
          {children}
        </Typography>
      ),
      // Paragraphs
      p: ({ children }: any) => (
        <Typography
          variant="body2"
          component="p"
          sx={{
            mb: 1,
            lineHeight: 1.5,
            fontSize: "0.75rem",
            color: "text.primary",
          }}
        >
          {children}
        </Typography>
      ),
      // Lists
      ul: ({ children }: any) => (
        <Box
          component="ul"
          sx={{
            pl: 2,
            mb: 1,
            "& li": {
              mb: 0.3,
              fontSize: "0.75rem",
              lineHeight: 1.4,
            },
          }}
        >
          {children}
        </Box>
      ),
      ol: ({ children }: any) => (
        <Box
          component="ol"
          sx={{
            pl: 2,
            mb: 1,
            "& li": {
              mb: 0.3,
              fontSize: "0.75rem",
              lineHeight: 1.4,
            },
          }}
        >
          {children}
        </Box>
      ),
      li: ({ children }: any) => (
        <Box
          component="li"
          sx={{
            fontSize: "0.75rem",
            lineHeight: 1.4,
            color: "text.primary",
          }}
        >
          {children}
        </Box>
      ),
      // Code blocks
      code: ({ node, inline, className, children, ...props }: any) => {
        if (inline) {
          return (
            <Box
              component="code"
              sx={{
                bgcolor: "grey.100",
                color: "primary.main",
                px: 0.5,
                py: 0.2,
                borderRadius: 0.5,
                fontSize: "0.7rem",
                fontFamily: "monospace",
              }}
              {...props}
            >
              {children}
            </Box>
          );
        }
        return (
          <Paper
            elevation={0}
            sx={{
              bgcolor: "grey.900",
              color: "common.white",
              p: 1.5,
              borderRadius: 1,
              mb: 1,
              overflow: "auto",
              fontSize: "0.7rem",
              fontFamily: "monospace",
              maxWidth: "100%",
            }}
          >
            <Box component="pre" sx={{ margin: 0, whiteSpace: "pre-wrap" }}>
              <code {...props}>{children}</code>
            </Box>
          </Paper>
        );
      },
      // Tables
      table: ({ children }: any) => (
        <Box
          sx={{
            overflow: "auto",
            mb: 1,
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 1,
          }}
        >
          <Box
            component="table"
            sx={{
              width: "100%",
              borderCollapse: "collapse",
              fontSize: "0.7rem",
            }}
          >
            {children}
          </Box>
        </Box>
      ),
      thead: ({ children }: any) => (
        <Box
          component="thead"
          sx={{
            bgcolor: "grey.100",
          }}
        >
          {children}
        </Box>
      ),
      th: ({ children }: any) => (
        <Box
          component="th"
          sx={{
            p: 1,
            textAlign: "left",
            fontWeight: 600,
            fontSize: "0.7rem",
            borderBottom: "1px solid",
            borderColor: "divider",
          }}
        >
          {children}
        </Box>
      ),
      td: ({ children }: any) => (
        <Box
          component="td"
          sx={{
            p: 1,
            fontSize: "0.7rem",
            borderBottom: "1px solid",
            borderColor: "divider",
          }}
        >
          {children}
        </Box>
      ),
      // Blockquotes
      blockquote: ({ children }: any) => (
        <Paper
          elevation={0}
          sx={{
            bgcolor: "grey.50",
            borderLeft: "4px solid",
            borderColor: "primary.main",
            pl: 2,
            py: 1,
            my: 1,
            fontStyle: "italic",
            fontSize: "0.75rem",
          }}
        >
          {children}
        </Paper>
      ),
      // Strong/Bold text
      strong: ({ children }: any) => (
        <Box
          component="strong"
          sx={{
            fontWeight: 600,
            color: "text.primary",
          }}
        >
          {children}
        </Box>
      ),
      // Emphasis/Italic text
      em: ({ children }: any) => (
        <Box
          component="em"
          sx={{
            fontStyle: "italic",
            color: "text.secondary",
          }}
        >
          {children}
        </Box>
      ),
      // Links
      a: ({ children, href }: any) => (
        <Box
          component="a"
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          sx={{
            color: "primary.main",
            textDecoration: "underline",
            fontSize: "0.75rem",
            "&:hover": {
              color: "primary.dark",
            },
          }}
        >
          {children}
        </Box>
      ),
      // Horizontal rule
      hr: () => (
        <Divider
          sx={{
            my: 2,
          }}
        />
      ),
      // Images
      img: ({ src, alt }: any) => (
        <Box
          component="img"
          src={src}
          alt={alt}
          sx={{
            maxWidth: "100%",
            height: "auto",
            borderRadius: 1,
            my: 1,
          }}
        />
      ),
    }),
    []
  );

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: message.isUser ? "flex-end" : "flex-start",
        alignItems: "flex-start",
        gap: 1,
        flexShrink: 0,
      }}
    >
      {!message.isUser && (
        <Avatar
          sx={{
            bgcolor: "primary.main",
            width: "1.5rem",
            height: "1.5rem",
            mt: 0.5,
            flexShrink: 0,
          }}
        >
          <BotIcon sx={{ fontSize: "0.75rem" }} />
        </Avatar>
      )}

      <Box
        sx={{
          maxWidth: "70%",
          display: "flex",
          flexDirection: "column",
          alignItems: message.isUser ? "flex-end" : "flex-start",
        }}
      >
        <Paper
          elevation={1}
          sx={{
            px: 2,
            py: 1,
            borderRadius: 2,
            bgcolor: message.isUser ? "primary.main" : "grey.100",
            color: message.isUser ? "white" : "text.primary",
            position: "relative",
            wordBreak: "break-word",
          }}
        >
          {message.isUser ? (
            <Typography
              variant="body2"
              sx={{ whiteSpace: "pre-wrap", fontSize: "0.75rem" }}
            >
              {message.text}
            </Typography>
          ) : (
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              rehypePlugins={[rehypeRaw]}
              components={markdownComponents}
            >
              {message.text}
            </ReactMarkdown>
          )}
          {message.isLoading && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                mt: 1,
              }}
            >
              <CircularProgress size={16} color="inherit" />
              <Typography variant="caption">Typing...</Typography>
            </Box>
          )}
        </Paper>
        <Typography
          variant="caption"
          sx={{
            mt: 0.5,
            color: "text.secondary",
            fontSize: "0.55rem",
          }}
        >
          {formatTime(message.timestamp)}
        </Typography>
      </Box>

      {message.isUser && (
        <Avatar
          sx={{
            bgcolor: "secondary.main",
            width: "1.5rem",
            height: "1.5rem",
            mt: 0.5,
            flexShrink: 0,
          }}
        >
          <PersonIcon sx={{ fontSize: "0.75rem" }} />
        </Avatar>
      )}
    </Box>
  );
});

// Separate Input Component to isolate state changes
const ChatInput = React.memo(
  ({
    onSendMessage,
    isLoading,
    onStop,
    lastUserMessage,
  }: {
    onSendMessage: (message: string) => Promise<void>;
    isLoading: boolean;
    onStop: () => void;
    lastUserMessage: string;
  }) => {
    const [inputText, setInputText] = useState("");
    const inputRef = useRef<HTMLInputElement>(null);

    const handleSendMessage = useCallback(async () => {
      if (!inputText.trim() || isLoading) return;

      const messageText = inputText.trim();
      setInputText("");
      await onSendMessage(messageText);
    }, [inputText, isLoading, onSendMessage]);

    const handleKeyPress = useCallback(
      (event: React.KeyboardEvent) => {
        if (event.key === "Enter" && !event.shiftKey) {
          event.preventDefault();
          handleSendMessage();
        }
      },
      [handleSendMessage]
    );

    return (
      <Box
        sx={{
          px: 2,
          py: 1,
          display: "flex",
          gap: 1,
          alignItems: "flex-end",
          bgcolor: "grey.50",
          borderTop: "1px solid",
          borderColor: "divider",
          flexShrink: 0,
        }}
      >
        <TextField
          ref={inputRef}
          fullWidth
          multiline
          maxRows={4}
          placeholder="Type your message here..."
          value={inputText}
          onChange={(e) => setInputText(e.target.value)}
          onKeyPress={handleKeyPress}
          disabled={isLoading}
          {...commonTextFieldProps}
          sx={{
            ...commonTextFieldProps.sx,
            "& .MuiInputBase-root": {
              borderRadius: 2,
              bgcolor: "white",
            },
          }}
        />
        {isLoading ? (
          <IconButton
            color="error"
            onClick={onStop}
            sx={{
              bgcolor: "primary.main",
              color: "white",
              "&:hover": {
                bgcolor: "primary.dark",
              },
              minWidth: 38,
              height: 35,
              flexShrink: 0,
            }}
          >
            <StopIcon sx={{ fontSize: "1rem" }} />
          </IconButton>
        ) : (
          <IconButton
            color="primary"
            onClick={handleSendMessage}
            disabled={!inputText.trim()}
            sx={{
              bgcolor: "primary.main",
              color: "white",
              "&:hover": {
                bgcolor: "primary.dark",
              },
              "&:disabled": {
                bgcolor: "grey.300",
                color: "grey.500",
              },
              minWidth: 38,
              height: 35,
              flexShrink: 0,
            }}
          >
            <SendIcon sx={{ fontSize: "1rem" }} />
          </IconButton>
        )}
      </Box>
    );
  }
);

const sendMessage = async (
  message: string,
  setIsLoading: (isLoading: boolean) => void,
  abortController?: AbortController
) => {
  try {
    setIsLoading(true);
    const token = await getValidToken();
    const url = `${process.env.REACT_APP_CHAT_BOT_API}ask-dynamic`;
    const response = await axios.post(
      url,
      {
        question: message,
      },
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        signal: abortController?.signal,
      }
    );
    return response?.data || null;
  } catch (error) {
    if (axios.isCancel(error)) {
      return "CANCELLED";
    }
    return null;
  } finally {
    setIsLoading(false);
  }
};

const Chat = () => {
  const navigate = useNavigate();
  const [messages, setMessages] = useState<Message[]>([
    {
      id: "1",
      text: "Hello! I'm your AI assistant. How can I help you today?",
      isUser: false,
      timestamp: new Date(),
    },
  ]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [thinkingMessage, setThinkingMessage] = useState("");
  const [lastUserMessage, setLastUserMessage] = useState<string>("");
  const thinkingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  // Auto-scroll to bottom when messages change
  const messagesEndRef = useChatScroll(messages);

  // Thinking messages for loading state
  const thinkingMessages = useMemo(
    () => [
      "Analyzing your request...",
      "Processing data...",
      "Generating response...",
      "Almost ready...",
    ],
    []
  );

  const startThinkingAnimation = useCallback(() => {
    let messageIndex = 0;
    setThinkingMessage(thinkingMessages[0]);

    thinkingIntervalRef.current = setInterval(() => {
      messageIndex = (messageIndex + 1) % thinkingMessages.length;
      setThinkingMessage(thinkingMessages[messageIndex]);
    }, 3000);
  }, [thinkingMessages]);

  const stopThinkingAnimation = useCallback(() => {
    if (thinkingIntervalRef.current) {
      clearInterval(thinkingIntervalRef.current);
      thinkingIntervalRef.current = null;
    }
    setThinkingMessage("");
  }, []);

  const stopCurrentMessage = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    stopThinkingAnimation();
    setIsLoading(false);
  }, [stopThinkingAnimation]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (thinkingIntervalRef.current) {
        clearInterval(thinkingIntervalRef.current);
      }
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  const handleSendMessage = useCallback(
    async (messageText: string) => {
      const userMessage: Message = {
        id: Date.now().toString(),
        text: messageText,
        isUser: true,
        timestamp: new Date(),
      };

      setMessages((prev) => [...prev, userMessage]);
      setLastUserMessage(messageText);
      setIsLoading(true);
      setError(null);
      startThinkingAnimation();

      // Create abort controller for this request
      abortControllerRef.current = new AbortController();

      try {
        const response = await sendMessage(
          messageText,
          setIsLoading,
          abortControllerRef.current
        );

        if (response === "CANCELLED") {
          // Message was cancelled, don't add any response
          return;
        } else if (response) {
          const botMessage: Message = {
            id: (Date.now() + 1).toString(),
            text: response,
            isUser: false,
            timestamp: new Date(),
          };
          setMessages((prev) => [...prev, botMessage]);
          setLastUserMessage(""); // Clear after successful response
        } else {
          const errorMessage: Message = {
            id: (Date.now() + 1).toString(),
            text: "I apologize, but I'm having trouble processing your request right now. Please try again in a moment, and if the problem persists, please contact support.",
            isUser: false,
            timestamp: new Date(),
          };
          setMessages((prev) => [...prev, errorMessage]);
          setError("Unable to get response from the server. Please try again.");
        }
      } catch (err) {
        stopThinkingAnimation();
        setError(err instanceof Error ? err.message : "Failed to send message");

        const errorMessage: Message = {
          id: (Date.now() + 1).toString(),
          text: "I'm sorry, but I encountered an unexpected error while processing your request. Please try again, and if the issue continues, please contact our support team.",
          isUser: false,
          timestamp: new Date(),
        };

        setMessages((prev) => [...prev, errorMessage]);
        setIsLoading(false);
      } finally {
        abortControllerRef.current = null;
      }
    },
    [startThinkingAnimation, stopThinkingAnimation]
  );

  // Memoized messages list to prevent unnecessary re-renders
  const messagesList = useMemo(
    () =>
      messages.map((message) => (
        <MemoizedMessage key={message.id} message={message} />
      )),
    [messages]
  );

  return (
    <Box
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        bgcolor: "#f5f5f5",
        p: 2,
        py: 0.5,
        overflow: "hidden",
        boxSizing: "border-box",
      }}
    >
      <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
        <IconButton title="Go Back" onClick={() => navigate(-1)}>
          <Clear sx={{ fontSize: "1.2rem" }} />
        </IconButton>
      </Box>
      <Paper
        elevation={1}
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          borderRadius: 2,
          overflow: "hidden",
          bgcolor: "white",
          minHeight: 0,
          height: "100%",
        }}
      >
        {/* Messages Container - Scrollable */}
        <Box
          ref={messagesEndRef}
          sx={{
            flex: 1,
            overflow: "auto",
            p: 2,
            display: "flex",
            flexDirection: "column",
            gap: 2,
            minHeight: 0,
            "&::-webkit-scrollbar": {
              width: "8px",
            },
            "&::-webkit-scrollbar-track": {
              background: "#f1f1f1",
              borderRadius: "4px",
            },
            "&::-webkit-scrollbar-thumb": {
              background: "#c1c1c1",
              borderRadius: "4px",
              "&:hover": {
                background: "#a8a8a8",
              },
            },
          }}
        >
          {messagesList}

          {/* Loading Indicator */}
          {isLoading && (
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-start",
                alignItems: "flex-start",
                gap: 1,
                flexShrink: 0,
              }}
            >
              <Avatar
                sx={{
                  bgcolor: "primary.main",
                  width: 32,
                  height: 32,
                  mt: 0.5,
                  flexShrink: 0,
                }}
              >
                <BotIcon sx={{ fontSize: 18 }} />
              </Avatar>
              <Paper
                elevation={1}
                sx={{
                  p: 2,
                  borderRadius: 2,
                  bgcolor: "grey.100",
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                <CircularProgress size={16} />
                <Typography variant="body2" color="text.secondary">
                  {thinkingMessage || "Thinking..."}
                </Typography>
              </Paper>
            </Box>
          )}
        </Box>

        {/* Divider */}
        <Divider />

        {/* Input Area - Optimized Component */}
        <ChatInput
          onSendMessage={handleSendMessage}
          isLoading={isLoading}
          onStop={stopCurrentMessage}
          lastUserMessage={lastUserMessage}
        />
      </Paper>
    </Box>
  );
};

export default Chat;
